import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const SPACING = 4;

interface Props {
  isOpen: boolean;
  /** Bounding rect of the trigger, for fixed positioning. */
  buttonRect: DOMRect | null;
  onClose: () => void;
  title: string;
  width?: number;
  maxHeight?: number;
  /**
   * Body renderer. Receives the popover panel element so inner controls
   * (DateTimePicker calendars, etc.) can portal into it via `portalContainer`
   * and layer above the click-catcher overlay instead of behind it at body
   * level.
   */
  children: (containerEl: HTMLElement | null) => React.ReactNode;
}

/**
 * Shared anchored-popover shell for Pro Details edit windows (Storage Cleared
 * style): a portal-to-body floating card positioned off the trigger's rect,
 * with a slate title bar + close button and a click-catcher overlay. Used by
 * the Grounded Dates and Hazmat editors so they read as one consistent
 * "tooltip" surface. (StorageClearedPopover predates this and keeps its own
 * copy of the chrome.)
 */
const AnchoredPopover: React.FC<Props> = ({
  isOpen,
  buttonRect,
  onClose,
  title,
  width = 300,
  maxHeight = 360,
  children,
}) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  if (!isOpen) return null;

  // Position: below the trigger, left-aligned; flip above if there isn't room.
  let top = 0;
  let left = 0;
  if (buttonRect) {
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const openUpward = spaceBelow < maxHeight && buttonRect.top > spaceBelow;
    top = openUpward
      ? Math.max(SPACING, buttonRect.top - maxHeight - SPACING)
      : buttonRect.bottom + SPACING;
    left = Math.min(buttonRect.left, window.innerWidth - width - SPACING);
    if (left < SPACING) left = SPACING;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99998]" onClick={onClose} />
      <div
        ref={setContainerEl}
        className="fixed z-[99999] bg-panel rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 flex flex-col"
        style={{ top: `${top}px`, left: `${left}px`, width: `${width}px`, maxHeight: `${maxHeight}px` }}
        role="dialog"
        aria-label={title}
      >
        <div className="px-3 py-2 bg-slate-700 dark:bg-slate-800 rounded-t-lg flex items-center justify-between shrink-0">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-300 hover:text-white"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children(containerEl)}
      </div>
    </>,
    document.body,
  );
};

export default AnchoredPopover;
